/**
 * @fileoverview Type definitions for the Firebase Integration Testing System
 *
 * This module provides types for creating human-readable integration tests
 * that run across two browser instances connected via Firebase.
 */

import { PieceType, MoveType, Position, GameState } from "../zodiacEngine/types";

/**
 * Simple 3-line test format:
 * - inputFEN: Starting board state
 * - move: Human-readable move notation ("Light Bear A1->B2")
 * - expectedFEN: Expected board state after move
 */
/**
 * Expected delta describing what should change during a move
 */
export interface ExpectedDelta {
  pieceMoved?: string;        // "Light Bear C1->C2"
  pieceCaptured?: string;     // "Dark Frog at B2"
  statusApplied?: string[];   // ["Poison applied to Light Bear"]
  statusChanged?: boolean;    // true if any status effect was added/removed
  bonusTurn?: boolean;        // true if same player gets another turn
  turnChanged?: boolean;      // true if turn passes to opponent
  gameEnded?: boolean;        // true if game ends
}

export interface IntegrationTest {
  name: string;
  description?: string;
  inputFEN: string;
  lightKing: PieceType;
  darkKing: PieceType;
  move: string; // "Light Bear C1->C2" or "Dark Frog A3->A5:lilypad"
  expectedFEN: string;
  expectedResult?: TestResult;
  expectedDelta?: ExpectedDelta;
  expectedStatus?: string[]; // e.g. ["Light Rhino: Infected", "Dark Bear: Zombified"]
  timeControl?: "sprint" | "standard" | "marathon" | "off";
  gameMode?: "competitive" | "quick" | "casual";
  // Multi-step sequence support
  isSequence?: boolean;
  steps?: SequenceStep[];
}

/**
 * A step in a multi-move test sequence
 */
export interface SequenceStep {
  move: string;
  expectedFEN: string;
  expectedDelta?: ExpectedDelta;
  expectedResult?: TestResult;
  note?: string;
  expectedStatus?: string[]; // e.g. ["Light Monkey: Poisoned", "Dark Fungi: Meadowed"]
}

/**
 * Extended test format for multi-move sequences
 */
export interface ExtendedIntegrationTest extends IntegrationTest {
  moves: TestMove[];
  assertions: TestAssertion[];
}

/**
 * A single move in a test sequence
 */
export interface TestMove {
  notation: string; // "Light Bear A1->B2"
  expectedIntermediateFEN?: string;
  expectedIntermediateResult?: TestResult;
}

/**
 * Assertion types for verifying game state
 */
export interface TestAssertion {
  type: "fen_equals" | "piece_at" | "piece_status" | "game_state" | "result_type";
  params: Record<string, unknown>;
}

/**
 * Game ending types - only tracks win/loss/draw conditions
 * Status effects, bonus turns, and captures are tracked via the delta output
 */
export type GameEndType =
  | false                        // No game ending
  | "victory_race_light"
  | "victory_race_dark"
  | "victory_zombierace_light"
  | "victory_zombierace_dark"
  | "victory_kingslayer_light"
  | "victory_kingslayer_dark"
  | "victory_timeout_light"
  | "victory_timeout_dark"
  | "draw_stalemate"
  | "draw_repetition";

/**
 * Result of a test move - only tracks game endings
 * Delta output handles: captures, status effects, bonus turns, etc.
 */
export interface TestResult {
  gameEnd: GameEndType;
  winner?: "light" | "dark";
}

/**
 * Parsed move from notation like "Light Bear A1->B2:capture"
 */
export interface ParsedMove {
  color: "light" | "dark";
  pieceType: PieceType;
  fromPosition: Position;
  toPosition: Position;
  moveType: MoveType;
  rawNotation: string;
  // Special command support (e.g., "timeout:light", "movezombieshadow")
  isSpecialCommand?: boolean;
  specialCommand?: "timeout" | "movezombieshadow";
  // Direction for split entangle moves (e.g., "entangle-up" -> entangleDirection: "up")
  // Used to disambiguate when octopus can land on two different squares while entangling the same target
  entangleDirection?: "up" | "down" | "left" | "right";
}

/**
 * Firebase test document structure
 */
export interface TestDocument {
  id: string;
  createdAt: number;
  status: TestStatus;

  // Test definition
  testSuite: string;
  testName: string;
  testConfig: IntegrationTest;

  // Participants
  lightPlayer: PlayerStatus;
  darkPlayer: PlayerStatus;

  // Game reference
  gameId: string;

  // Synchronization
  syncPoints: Record<string, SyncPoint>;

  // Assertions from both browsers
  assertions: Record<string, AssertionResult>;

  // Final result
  result?: FinalTestResult;
}

export type TestStatus = "waiting" | "ready" | "running" | "completed" | "failed" | "timeout";

export interface PlayerStatus {
  browserId: string;
  connected: boolean;
  ready: boolean;
  lastSeen: number;
}

export interface SyncPoint {
  light: boolean;
  dark: boolean;
  lightTimestamp?: number;
  darkTimestamp?: number;
}

export interface AssertionResult {
  lightPassed: boolean;
  darkPassed: boolean;
  lightValue: unknown;
  darkValue: unknown;
  lightTimestamp: number;
  darkTimestamp: number;
}

export interface FinalTestResult {
  passed: boolean;
  duration: number;
  errorMessage?: string;
  failedAssertions: string[];
  actualFEN?: string;
  actualResult?: TestResult;
}

/**
 * Test suite containing multiple tests
 */
export interface TestSuite {
  name: string;
  description: string;
  tests: IntegrationTest[];
}

// ============================================================================
// MATCHMAKING TESTS - Automated service-level tests against Firebase
// ============================================================================

/**
 * A matchmaking test runs actual Firebase service calls and verifies results.
 * These test account management, rating changes, game flows, etc.
 */
export interface MatchmakingTest {
  name: string;
  description: string;
  category: MatchmakingTestCategory;
  /** The test function that runs assertions - returns true if passed */
  run: () => Promise<MatchmakingTestResult>;
}

export type MatchmakingTestCategory =
  | "placement"
  | "rating"
  | "rematch"
  | "account"
  | "popup"
  | "clock";

/**
 * Result of a matchmaking test
 */
export interface MatchmakingTestResult {
  passed: boolean;
  message: string;
  details?: {
    expected?: unknown;
    actual?: unknown;
    error?: string;
  };
  duration: number;
}

/**
 * Test suite for matchmaking/service tests
 */
export interface MatchmakingTestSuite {
  name: string;
  description: string;
  tests: MatchmakingTest[];
  isMatchmaking: true;  // Discriminator
}

/**
 * Role in a test session
 */
export type TestRole = "light" | "dark" | "observer";

/**
 * Status of test execution
 */
export interface TestExecutionState {
  role: TestRole;
  testId: string;
  gameId: string;
  status: TestStatus;
  currentTest?: IntegrationTest;
  partnerConnected: boolean;
  partnerReady: boolean;
  error?: string;
}

/**
 * Move type specifier mapping
 */
export const MOVE_TYPE_SPECIFIERS: Record<string, MoveType> = {
  "": MoveType.regular,
  regular: MoveType.regular,
  capture: MoveType.capture,
  lilypad: MoveType.jumppad,
  swing: MoveType.swing,
  rotate: MoveType.rotate,
  lane: MoveType.lane,
  trample: MoveType.trample,
  ink: MoveType.ink,
  traverse: MoveType.traverse,
  infect: MoveType.infect,
  endturn: MoveType.endTurn,
  consume: MoveType.consume,
  faint: MoveType.faint,
  leap: MoveType.leap,
  house: MoveType.house,
  entangle: MoveType.noMove,
  tripleentangle: MoveType.tripleentangle,
  // Directional entangle variants for split entangle (octopus lands on different square)
  "entangle-up": MoveType.noMove,
  "entangle-down": MoveType.noMove,
  "entangle-left": MoveType.noMove,
  "entangle-right": MoveType.noMove,
};

/**
 * Piece type name mapping (for parsing)
 */
export const PIECE_TYPE_NAMES: Record<string, PieceType> = {
  bear: PieceType.Bear,
  crane: PieceType.Crane,
  frog: PieceType.Frog,
  fungi: PieceType.Fungi,
  goat: PieceType.Goat,
  monkey: PieceType.Monkey,
  octopus: PieceType.Octopus,
  penguin: PieceType.Penguin,
  rhino: PieceType.Rhino,
  snake: PieceType.Snake,
  tree: PieceType.Tree,
  wolf: PieceType.Wolf,
  virus: PieceType.Virus,
};
